<script setup lang="ts">
import { URL_IMAGE } from '@/config/constants'
import { categoryIdBus } from '@/event/category/categoryIdBus'
import { languageIdBus } from '@/event/languageIdBus'
import { productAssociated } from '@/event/product/productAssociated'
import { productBus } from '@/event/product/productBus'
import { productIdBus } from '@/event/product/productIdBus'
import { cardTheme } from '@/event/theme/card'
import {
    isOtherThemeHappy,
    isThemeDefault,
    isThemeHappy,
    theme
} from '@/event/theme/theme'
import CardDefaultProductHappy from '@/happyTheme/components/catalogCategory/cards/CardDefaultProductHappy.vue'
import CardEmptyProductHappy from '@/happyTheme/components/catalogCategory/cards/CardEmptyProductHappy.vue'
import CardFullProductHappy from '@/happyTheme/components/catalogCategory/cards/CardFullProductHappy.vue'
import CardRectProductHappy from '@/happyTheme/components/catalogCategory/cards/CardRectProductHappy.vue'
import NoCardProductHappy from '@/happyTheme/components/catalogCategory/cards/NoCardProductHappy.vue'
import { showAlertValidateProductHappy } from '@/happyTheme/event/product/showAlertValidateProductHappy'
import { showProductAssociated } from '@/happyTheme/event/product/showProductAssociated'
import { truncateText } from '@/happyTheme/utils/product/truncateText'
import type { Product } from '@/storage/products'
import { formatPrice } from '@/event/locale'
import { convertPriceToEuro } from '@/utils/price/convertPrice'
import { extractRelatedOptions } from '@/utils/product/extractRelatedOptions'
import { getProductDescription } from '@/utils/product/getProductDescription'
import { getProductDescriptionAssociated } from '@/utils/product/getProductDescriptionProductAssociated'
import { getProductName } from '@/utils/product/getProductName'
import { updateProduct } from '@/utils/product/updateProduct'
import { computed, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const expandedDescriptions = ref<boolean[]>([])
const descriptionWidths = ref<Record<number, number>>({})
const descriptionSpaces = ref<Record<number, number>>({})
const descriptionRefs = ref<HTMLDivElement[]>([])
const resizeObservers = ref<Record<number, ResizeObserver>>({})

function updateDescriptionWidth(index: number) {
    nextTick(() => {
        const container = descriptionRefs.value[index]
        if (!container) return

        const svg = container
            .closest('.div-product-associated-happy')
            ?.querySelector('.svg-card-product-associated-happy') as SVGElement

        if (!svg) return

        const svgRect = svg.getBoundingClientRect()
        const paddingLeft = window.getComputedStyle(container).paddingLeft
        const paddingRight = window.getComputedStyle(container).paddingRight
        const availableWidth =
            svgRect.width - parseFloat(paddingLeft) - parseFloat(paddingRight) - 20

        descriptionWidths.value[index] = availableWidth

        const h2Element = container.querySelector(
            '.h2-card-name-associated-happy'
        ) as HTMLElement
        if (h2Element) {
            const h2Height = h2Element.getBoundingClientRect().height
            const containerHeight = container.getBoundingClientRect().height
            const availableSpace = containerHeight - h2Height - 10
            descriptionSpaces.value[index] = availableSpace
        }
    })
}

function selectProductAssociated(product: Product) {
    // showSelectedModalProductHappy.value = false
    try {
        productIdBus.value = product.product_id
        categoryIdBus.value = product.category_id

        nextTick(() => {
            updateProduct()

            const productFound = productBus.value
            if (!productFound || !productFound.options) return
            extractRelatedOptions(productFound.options)

            expandedDescriptions.value = expandedDescriptions.value.map(() => false)
            showProductAssociated.value = false
            showAlertValidateProductHappy.value = false
        })
    } catch (error) {
        console.error('Error selecting product associated:', error)
    }
}

const setDescriptionRef = (el: HTMLDivElement | null) => {
    if (el) {
        if (!descriptionRefs.value.includes(el)) {
            descriptionRefs.value.push(el)
            updateDescriptionWidth(descriptionRefs.value.length - 1)

            if (!resizeObservers.value[descriptionRefs.value.length - 1]) {
                resizeObservers.value[descriptionRefs.value.length - 1] =
                    new ResizeObserver(() => {
                        updateDescriptionWidth(descriptionRefs.value.length - 1)
                    })
            }
            resizeObservers.value[descriptionRefs.value.length - 1].observe(el)
        }
    } else {
        const index = descriptionRefs.value.findIndex(
            (ref) => ref === null || !document.contains(ref)
        )
        if (index > -1) {
            descriptionRefs.value.splice(index, 1)
            if (resizeObservers.value[index]) {
                resizeObservers.value[index].disconnect()
                delete resizeObservers.value[index]
            }
        }
        // if (resizeObservers.value[descriptionRefs.value.length - 1]) {
        //     resizeObservers.value[descriptionRefs.value.length - 1].disconnect()
        //     delete resizeObservers.value[descriptionRefs.value.length - 1]
        // }
        // delete descriptionRefs.value[descriptionRefs.value.length - 1]
    }
}

const truncatedDescriptions = computed(() => {
    if (!productAssociated.value || productAssociated.value.length === 0) return []
    return productAssociated.value.map((pa, index) => {
        if (!pa) return ''
        const fullDesc = getProductDescription(pa)
        const maxWidth = descriptionWidths.value[index] || 0
        return truncateText(fullDesc, maxWidth)
    })
    // const fullDesc = getProductDescription(productAssociated.value[0])
    // const maxWidth = descriptionWidths.value[index] || 0
    // return truncateText(fullDesc, maxWidth)
})
</script>

<template>
    <section
        class="section-product-associated-main-content"
        v-if="productAssociated.length !== 0"
    >
        <!-- Theme Happy -->
        <section
            class="section-content-associated-happy"
            v-if="isThemeHappy || isOtherThemeHappy"
        >
            <h3 class="h3-content-associated-happy">{{ t('productAddedSuccess') }}</h3>

            <h3 class="h3-product-associated-happy">
                {{ t('productAssociatedSuggestion') }}
            </h3>

            <section
                class="section-product-associated-content-happy"
                :class="{
                    'gap-2': cardTheme !== 'rect',
                    'gap-y-4': cardTheme === 'rect'
                }"
            >
                <div
                    v-for="(pa, i) in productAssociated"
                    :key="i"
                    class="div-product-associated-happy"
                    @click.stop="selectProductAssociated(pa)"
                >
                    <CardDefaultProductHappy
                        v-if="cardTheme === 'default' || cardTheme === ''"
                        :product="pa"
                        :index="i"
                        :truncated-descriptions="truncatedDescriptions"
                        :props-func="setDescriptionRef"
                    />

                    <CardRectProductHappy
                        :product="pa"
                        :index="i"
                        :truncated-descriptions="truncatedDescriptions"
                        :props-func="setDescriptionRef"
                        v-else-if="cardTheme === 'rect'"
                    />

                    <CardEmptyProductHappy
                        v-else-if="cardTheme === 'empty'"
                        :product="pa"
                        :description="truncatedDescriptions[i]"
                    />
                    <CardFullProductHappy
                        v-else-if="cardTheme === 'full'"
                        :product="pa"
                        :description="truncatedDescriptions[i]"
                    />

                    <NoCardProductHappy
                        v-else-if="cardTheme === 'no-card'"
                        :product="pa"
                    />
                </div>
            </section>
        </section>

        <!-- Theme Default -->

        <section class="w-full flex flex-col flex-wrap" v-if="isThemeDefault">
            <h3 class="h3-main-product-associated">{{ t('productAssociatedTitle') }}</h3>
            <section class="section-product-associated-content gap-2" :class="{}">
                <div
                    v-for="(pa, i) in productAssociated"
                    :key="i"
                    class="div-for-product-associated"
                >
                    <router-link
                        :to="{
                            name: 'product',
                            params: {
                                category_id: pa.category_id,
                                product_id: pa.product_id
                            }
                        }"
                        class="router-link-product-associated"
                    >
                        <img
                            :src="`${URL_IMAGE}${pa.image}`"
                            class="img-for-product-associated"
                            :alt="getProductDescription(pa)"
                            loading="lazy"
                        />
                        <h3 class="h3-product-associated">{{ getProductName(pa) }}</h3>
                        <div class="div-informations-product-associated">
                            <p class="p-product-associated">
                                {{ getProductDescriptionAssociated(pa) }}
                            </p>
                            <p>{{ formatPrice(convertPriceToEuro(pa.price)) }}</p>
                        </div>
                    </router-link>
                </div>
            </section>
        </section>
    </section>
</template>

<style scoped>
.section-product-associated-main-content {
    @apply mt-3 rounded-md flex flex-col gap-2;
}
/** Theme Happy */
.section-content-associated-happy {
    /* refactored */
    @apply text-t-text;
}
/** no card */

.product-associated-happy-no-card {
    @apply w-full flex flex-col justify-between h-full gap-1 rounded-md py-2 px-1;
}
.img-happy-no-card {
    @apply w-full h-28 object-contain rounded-t-md;
}
.div-description-product-associated-no-card-happy {
    /* refactored */
    @apply flex justify-between px-3 mt-2 text-t-text;
}
.text-product-associated-name-no-card-happy {
    @apply font-semibold text-wrap text-sm;
}
.text-description-product-associated-no-card-happy {
    @apply px-3 text-xs line-clamp-2  text-ellipsis;
    /*   @apply happy-light:text-text-dark-happy-light;
    @apply happy-dark:text-text-dark-happy-light;
    @apply happy-dark:text-bg-happy-light;
    @apply happy-urban-dark-red:text-bg-happy-light;
    @apply happy-orange-light:text-urban-dark;
    @apply happy-orange-dark:text-bg-happy-light; */
}
.text-price-product-associated-no-card-happy {
    /* refactored */
    @apply font-semibold text-nowrap text-sm px-2;
    @apply text-t-accent;
}
.div-button-select-product-associated-no-card-full-happy {
    /* refactored */
    @apply rounded-md flex items-center justify-center shadow-sm font-semibold p-0.5;
    @apply bg-t-accent text-t-on-accent;
}

@media (hover) {
    .product-associated-happy-no-card:hover {
        @apply scale-[1.03];
        @apply bg-red-eronor;
        @apply happy-urban-dark-red:bg-urban-red-dark;
        @apply happy-orange-light:bg-bg-orange-dark;
        @apply happy-orange-dark:bg-bg-orange-dark;
        @apply happy-fresh-mint:bg-bg-mint-dark;
        @apply happy-bee-light:bg-urban-dark;
        @apply happy-bee-dark:bg-bee-yellow-dark;
        @apply happy-ifo-dark:bg-t-accent-hover;
        @apply happy-earth-stone:bg-earth-card-dark;
        @apply happy-scorched-earth:bg-scorched-earth-red;
        @apply happy-sand-sky:bg-sand-blue-dark;
        @apply happy-gray-sand:bg-gray-sand-gray-dark;
        @apply happy-purple-gradient:bg-bg-purple-dark;
    }
    .product-associated-happy-no-card:hover .text-product-associated-name-no-card-happy,
    .product-associated-happy-no-card:hover
        .text-description-product-associated-no-card-happy {
        @apply text-bg-happy-light;
    }

    .product-associated-happy-no-card:hover .text-price-product-associated-no-card-happy {
        @apply text-bg-happy-light;
        @apply happy-urban-dark-red:text-urban-dark;
        @apply happy-urban-light-red:text-urban-dark;
        @apply happy-orange-light:text-urban-dark;
        @apply happy-orange-dark:text-urban-dark;
        @apply happy-bee-light:text-urban-red;
        @apply happy-bee-dark:text-urban-dark;
        @apply happy-ifo-dark:text-t-text;
        @apply happy-sand-sky:text-urban-dark;
        @apply happy-gray-sand:text-urban-dark;
        @apply happy-purple-gradient:text-bee-yellow;
    }

    .product-associated-happy-no-card:hover
        .div-button-select-product-associated-no-card-full-happy {
        @apply happy-bee-light:bg-bee-yellow happy-bee-light:text-urban-dark;
        @apply happy-earth-stone:bg-earth-card;
        @apply happy-sand-sky:bg-sand-blue happy-sand-sky:text-urban-dark;
        @apply happy-gray-sand:bg-gray-sand-gray-light happy-gray-sand:text-urban-dark;
    }
}

/** */
.section-product-associated-content-happy {
    @apply grid grid-cols-2 justify-items-center;
}

/* .section-content-associated-happy {
    @apply w-full flex flex-col flex-wrap;
} */

.h3-content-associated-happy {
    @apply font-semibold  text-lg pb-2 px-1;
}

.h3-product-associated-happy {
    @apply font-semibold text-sm pb-6 px-1;
}

.div-product-associated-happy {
    @apply w-full flex flex-col items-center justify-between;
}

.router-link-product-associated-happy {
    @apply w-full flex flex-col items-center flex-grow relative;
    @apply happy-light:shadow-text-dark-happy-light;
    @apply happy-dark:shadow-bg-happy-light;
}

.svg-card-product-associated-happy {
    @apply w-full h-auto;
    @apply happy-light:shadow-text-dark-happy-light happy-light:fill-bg-happy-light;
    @apply happy-dark:shadow-bg-happy-light happy-dark:fill-text-basket-option-light-happy;
    @apply happy-urban-dark-red:shadow-bg-happy-light happy-urban-dark-red:fill-urban-red;
    @apply happy-urban-light-red:shadow-urban-dark happy-urban-light-red:fill-urban-red;
    @apply happy-orange-light:fill-bg-orange happy-orange-light:shadow-urban-dark;
    @apply happy-orange-dark:fill-bg-orange happy-orange-dark:shadow-urban-dark;
    @apply happy-fresh-mint:fill-bg-mint happy-fresh-mint:shadow-urban-dark;
    @apply happy-bee-light:fill-bg-happy-light happy-bee-light:shadow-urban-dark;
    @apply happy-bee-dark:fill-bee-yellow happy-bee-dark:shadow-urban-dark;
    @apply happy-ifo-dark:fill-t-accent happy-ifo-dark:shadow-black/20;
    @apply happy-earth-stone:fill-earth-card happy-earth-stone:shadow-urban-dark;
    @apply happy-scorched-earth:fill-scorched-earth-yellow happy-scorched-earth:shadow-urban-dark;
    @apply happy-sand-sky:fill-sand-blue-dark happy-sand-sky:shadow-urban-dark;
    @apply happy-gray-sand:fill-gray-sand-gray-dark happy-gray-sand:shadow-urban-dark;
    @apply happy-purple-gradient:fill-pink-dark happy-purple-gradient:shadow-urban-dark;
}

.div-product-associated-happy:active .svg-card-product-associated-happy,
.div-product-associated-happy:focus .svg-card-product-associated-happy {
    transform: scale(1.05);
    transition: all 0.3s ease;
    @apply happy-light:fill-border-red-happy-light;
    @apply happy-dark:fill-border-red-happy-light;
    @apply happy-urban-dark-red:fill-urban-red-dark;
    @apply happy-urban-light-red:fill-urban-red-dark;
    @apply happy-orange-light:fill-bg-orange-dark;
    @apply happy-orange-dark:fill-bg-orange-dark;
    @apply happy-fresh-mint:fill-bg-mint-dark;
    @apply happy-bee-light:fill-bee-yellow-dark;
    @apply happy-bee-dark:fill-bee-yellow-dark;
    @apply happy-ifo-dark:fill-t-accent-hover;
    @apply happy-earth-stone:fill-earth-card-dark;
    @apply happy-scorched-earth:fill-scorched-earth-red;
    @apply happy-sand-sky:fill-sand-blue;
    @apply happy-gray-sand:fill-gray-sand-gray-light;
    @apply happy-purple-gradient:fill-bg-purple-dark;
}

.div-product-associated-happy:active .svg-card-price-associated-happy,
.div-product-associated-happy:focus .svg-card-price-associated-happy {
    @apply happy-light:fill-mouse-enter-red-happy-light;
    @apply happy-dark:fill-mouse-enter-red-happy-light;
    @apply happy-urban-dark-red:fill-urban-dark;
    @apply happy-urban-light-red:fill-urban-dark;
    @apply happy-fresh-mint:fill-urban-dark;
    @apply happy-bee-light:fill-urban-red-dark;
    @apply happy-bee-dark:fill-urban-red-dark;
    @apply happy-ifo-dark:fill-t-accent-hover;
}

.div-product-associated-happy:active .div-card-description-associated-happy,
.div-product-associated-happy:focus .div-card-description-associated-happy {
    @apply happy-light:text-bg-happy-light;
    @apply happy-dark:text-bg-happy-light;
    @apply happy-urban-dark-red:text-bg-happy-light;
    @apply happy-urban-light-red:text-bg-happy-light;
    @apply happy-fresh-mint:text-bg-happy-light;
    @apply happy-bee-light:text-bg-happy-light;
    @apply happy-bee-dark:text-bg-happy-light;
    @apply happy-ifo-dark:text-t-text;
    @apply happy-earth-stone:text-bg-happy-light;
    @apply happy-scorched-earth:text-bg-happy-light;
    @apply happy-sand-sky:text-urban-dark;
}

.div-product-associated-happy:active .p-card-price-associated-happy,
.div-product-associated-happy:focus .p-card-price-associated-happy {
    @apply happy-light:text-bg-happy-light;
    @apply happy-dark:text-bg-happy-light;
    @apply happy-urban-dark-red:text-bg-happy-light;
    @apply happy-urban-light-red:text-bg-happy-light;
    @apply happy-fresh-mint:text-bg-happy-light;
    @apply happy-bee-light:text-bg-happy-light;
    @apply happy-bee-dark:text-bg-happy-light;
    @apply happy-ifo-dark:text-t-text;
    @apply happy-earth-stone:text-bg-happy-light;
    @apply happy-scorched-earth:text-bg-happy-light;
    @apply happy-sand-sky:text-urban-dark;
}

.img-for-product-associated-happy {
    @apply inset-0 -top-6 -left-2 w-5/6 h-3/5 object-contain absolute z-10;
}

.div-informations-product-associated-happy {
    @apply absolute z-10 top-[28%] right-[3%] flex items-center justify-center;
    /* @apply happy-light:text-text-dark-happy-light;
    @apply happy-dark:text-text-dark-happy-light; */
}

.div-rect-informations-product-associated-happy {
    @apply absolute z-10 top-[20%] right-[8%] flex items-center justify-center;
}

.p-product-associated-happy {
    @apply absolute inset-0 flex text-sm items-center text-center m-auto font-semibold;
    /* @apply happy-light:text-bg-happy-light;
    @apply happy-dark:text-bg-happy-light; */
    /* @apply happy-urban-dark-red:text-bg-happy-light; */
    /* @apply happy-urban-light-red:text-bg-happy-light; */
}

.div-card-price-associated-happy {
    @apply absolute z-20 top-[28%] right-[3%] flex flex-col items-center;
    @apply cursor-pointer;
}

/** */

.svg-card-product-rect-associated-happy {
    @apply w-full h-auto;
    @apply happy-light:shadow-text-dark-happy-light happy-light:fill-bg-happy-light;
    @apply happy-dark:shadow-bg-happy-light happy-dark:fill-text-basket-option-light-happy;
    @apply happy-urban-dark-red:shadow-bg-happy-light happy-urban-dark-red:fill-urban-red;
    @apply happy-urban-light-red:shadow-urban-dark happy-urban-light-red:fill-urban-red;
    @apply happy-orange-light:fill-bg-orange happy-orange-light:shadow-urban-dark;
    @apply happy-orange-dark:fill-bg-orange happy-orange-dark:shadow-urban-dark;
    @apply happy-fresh-mint:fill-bg-mint happy-fresh-mint:shadow-urban-dark;
    @apply happy-bee-light:fill-bg-happy-light happy-bee-light:shadow-urban-dark;
    @apply happy-bee-dark:fill-bee-yellow happy-bee-dark:shadow-urban-dark;
    @apply happy-ifo-dark:fill-t-accent happy-ifo-dark:shadow-black/20;
    @apply happy-earth-stone:fill-earth-card happy-earth-stone:shadow-urban-dark;
    @apply happy-scorched-earth:fill-scorched-earth-yellow happy-scorched-earth:shadow-urban-dark;
    @apply happy-sand-sky:fill-sand-blue-dark happy-sand-sky:shadow-urban-dark;
    @apply happy-gray-sand:fill-gray-sand-gray-dark happy-gray-sand:shadow-urban-dark;
    @apply happy-purple-gradient:fill-pink-dark happy-purple-gradient:shadow-urban-dark;
}

/** */

.svg-card-price-associated-happy,
.svg-card-price-associated-happy-rect {
    @apply w-16 fill-border-gray-happy-light;
    @apply happy-gray-sand:fill-bg-gray-sand;
}

.p-card-price-associated-happy {
    @apply absolute text-sm inset-0 flex items-center justify-center text-center m-auto font-semibold;
    @apply happy-light:text-text-dark-happy-light;
    @apply happy-dark:text-text-dark-happy-light;
    @apply happy-urban-dark-red:text-urban-dark;
    @apply happy-urban-light-red:text-urban-dark;
    @apply happy-orange-light:text-urban-dark;
    @apply happy-orange-dark:text-urban-dark;
    @apply happy-bee-light:text-urban-dark;
    @apply happy-bee-dark:text-urban-dark;
    @apply happy-ifo-dark:text-t-text;
    @apply happy-sand-sky:text-bg-happy-light;
    @apply happy-scorched-earth:text-urban-dark;
    @apply happy-purple-gradient:text-urban-dark;
}

.div-card-description-associated-happy {
    @apply absolute top-[45%] bottom-3 transform text-center min-w-full text-sm font-semibold flex flex-col items-start pl-3;
    @apply happy-light:text-text-dark-happy-light;
    @apply happy-dark:text-bg-happy-light;
    @apply happy-urban-dark-red:text-bg-happy-light;
    @apply happy-urban-light-red:text-bg-happy-light;
    @apply happy-orange-light:text-bg-happy-light;
    @apply happy-bee-dark:text-urban-dark;
    @apply happy-ifo-dark:text-t-text;
    @apply happy-sand-sky:text-bg-happy-light;
}

.div-card-name-associated-happy {
    @apply overflow-hidden w-[97%];
}

.h2-card-name-associated-happy {
    @apply text-wrap text-start w-3/5 text-ellipsis;
}

.p-card-name-associated-happy {
    @apply text-start text-xs w-4/6 mt-1 overflow-hidden;
}

@media (hover: hover) {
    .div-product-associated-happy:hover .svg-card-product-associated-happy,
    .div-product-associated-happy:hover .svg-card-product-rect-associated-happy {
        transform: scale(1.03);
        transition: all 0.3s ease;
        @apply happy-light:fill-border-red-happy-light;
        @apply happy-dark:fill-border-red-happy-light;
        @apply happy-urban-dark-red:fill-urban-red-dark;
        @apply happy-urban-light-red:fill-urban-red-dark;
        @apply happy-orange-light:fill-bg-orange-dark;
        @apply happy-orange-dark:fill-bg-orange-dark;
        @apply happy-fresh-mint:fill-bg-mint-dark;
        @apply happy-bee-light:fill-bee-yellow-dark;
        @apply happy-bee-dark:fill-bee-yellow-dark;
        @apply happy-ifo-dark:fill-t-accent-hover;
        @apply happy-earth-stone:fill-earth-card-dark;
        @apply happy-scorched-earth:fill-scorched-earth-red;
        @apply happy-sand-sky:fill-sand-blue;
        @apply happy-gray-sand:fill-gray-sand-gray-light;
        @apply happy-purple-gradient:fill-bg-purple-dark;
    }

    .div-product-associated-happy:hover .svg-card-price-associated-happy,
    .div-product-associated-happy:hover .svg-card-price-associated-happy-rect {
        /* @apply happy-light:fill-mouse-enter-red-happy-light;
        @apply happy-dark:fill-mouse-enter-red-happy-light;
        @apply happy-urban-dark-red:fill-urban-dark;
        @apply happy-urban-light-red:fill-urban-dark;
        @apply happy-fresh-mint:fill-urban-dark;
        @apply happy-bee-light:fill-urban-red-dark;
        @apply happy-bee-dark:fill-urban-red-dark; */
        @apply fill-mouse-enter-red-happy-light;
        @apply happy-urban-dark-red:fill-urban-dark;
        @apply happy-urban-light-red:fill-urban-dark;
        @apply happy-orange-light:fill-text-dark-happy-light;
        @apply happy-orange-dark:fill-text-dark-happy-light;
        @apply happy-fresh-mint:fill-urban-dark;
        @apply happy-earth-stone:fill-earth-card;
        @apply happy-scorched-earth:fill-scorched-earth-yellow;
        @apply happy-sand-sky:fill-sand-blue-dark;
        @apply happy-gray-sand:fill-border-gray-happy-light;
        @apply happy-purple-gradient:fill-bg-purple-light;
    }

    .div-product-associated-happy:hover .div-card-description-associated-happy {
        @apply happy-light:text-bg-happy-light;
        @apply happy-dark:text-bg-happy-light;
        @apply happy-urban-dark-red:text-bg-happy-light;
        @apply happy-urban-light-red:text-bg-happy-light;
        @apply happy-fresh-mint:text-bg-happy-light;
        @apply happy-bee-light:text-bg-happy-light;
        @apply happy-bee-dark:text-bg-happy-light;
        @apply happy-ifo-dark:text-t-text;
        @apply happy-earth-stone:text-bg-happy-light;
        @apply happy-scorched-earth:text-bg-happy-light;
        @apply happy-sand-sky:text-urban-dark;
    }

    .div-product-associated-happy:hover .p-card-price-associated-happy {
        @apply happy-light:text-bg-happy-light;
        @apply happy-dark:text-bg-happy-light;
        @apply happy-urban-dark-red:text-bg-happy-light;
        @apply happy-urban-light-red:text-bg-happy-light;
        @apply happy-orange-light:text-bg-happy-light;
        @apply happy-orange-dark:text-bg-happy-light;
        @apply happy-fresh-mint:text-bg-happy-light;
        @apply happy-bee-light:text-bg-happy-light;
        @apply happy-bee-dark:text-bg-happy-light;
        @apply happy-ifo-dark:text-t-text;
        @apply happy-earth-stone:text-urban-dark;
        @apply happy-scorched-earth:text-urban-dark;
        @apply happy-sand-sky:text-urban-dark;
    }
}

/** Theme Default */

.h3-main-product-associated {
    @apply font-semibold text-lg flex w-full items-center pb-2;
}

.section-product-associated-content {
    @apply grid grid-cols-2;
    grid-auto-rows: minmax(11rem, auto);
}

.div-for-product-associated {
    @apply flex flex-col items-center justify-between;
}

.router-link-product-associated {
    @apply w-full flex flex-col items-center  p-3 flex-grow bg-slate-eronor rounded-md relative;
}

.img-for-product-associated {
    @apply w-5/6 h-3/5 object-contain  z-10;
}

.h3-product-associated {
    @apply w-full font-medium;
}

.div-informations-product-associated {
    @apply w-full font-normal;
}

.p-product-associated {
    @apply text-[10px];
}

@media screen and (min-width: 600px) {
    .section-product-associated-content {
        @apply grid-cols-3;
    }
}

@media screen and (min-width: 800px) {
    .section-product-associated-content {
        @apply grid-cols-4;
    }
}
</style>
